// src/utils/logoutUtils.ts
import { authInterceptor } from '../../services/Authentication/authInterceptor';
import { secureStorage } from '../../services/Storage/secureStorage';
import { tokenMonitor } from '../../services/Authentication/tokenMonitor';
import { config } from '../../config/env';
import { supabase } from '../../lib/supabase';
import { logger } from '../logger';

export interface LogoutOptions {
  showMessage?: boolean;
  reason?: 'user_initiated' | 'token_expired' | 'session_invalid' | 'security_breach';
}

export class LogoutManager {
  /**
   * Comprehensive logout function that handles all cleanup
   */
  static async logout(options: LogoutOptions = {}): Promise<void> {
    const {
      showMessage = false,
      reason = 'user_initiated'
    } = options;

    try {
      logger.log(`Logout initiated: ${reason}`);

      // 0. Set logout flag to prevent automatic re-authentication
      localStorage.setItem('logout_in_progress', 'true');

      // 1. Stop token monitoring immediately
      tokenMonitor.stopMonitoring();
      logger.log('Token monitoring stopped');

      // 2. Call backend logout endpoint (server-side session invalidation)
      try {
        // Try to use user's access token if available, otherwise use anon key
        const accessToken = authInterceptor.getAccessToken();
        const authHeader = accessToken 
          ? `Bearer ${accessToken}` 
          : `Bearer ${config.supabase.anonKey}`;
          
        await fetch(`${config.api.baseUrl}/auth/logout`, {
          method: "POST",
          headers: {
            Authorization: authHeader,
          },
        });
        logger.log('Backend logout successful');
      } catch (error) {
        console.warn('Backend logout failed, continuing with cleanup:', error);
      }

      // 3. Call Supabase logout (client-side session invalidation) BEFORE
      // wiping local storage — the SDK needs its own intact session data to
      // sign out properly. Guarded with a timeout: if signOut() ever hangs
      // (e.g. because storage was already cleared from under it), that must
      // never block the rest of logout from completing.
      try {
        await Promise.race([
          supabase.auth.signOut({ scope: 'global' }),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Supabase signOut timed out')), 3000)),
        ]);
        logger.log('Supabase logout successful');
      } catch (error) {
        console.warn('Supabase logout failed or timed out, continuing with cleanup:', error);
      }

      // 4. Clear all local storage now that Supabase has had its chance to
      // sign out cleanly.
      this.clearAllAuthData();
      logger.log('All auth data cleared');

      // 5. Show logout message if requested
      if (showMessage) {
        this.showLogoutMessage(reason);
      }

      // 6. Clear logout flag (navigation handled by calling component)
      logger.log('Logout process completed successfully');
      localStorage.removeItem('logout_in_progress');
      logger.log('LogoutManager.logout() function completed');

    } catch (error) {
      console.error('Logout process failed:', error);
      
      // Force cleanup even if logout process fails
      localStorage.removeItem('logout_in_progress');
      this.clearAllAuthData();
      // Navigation will be handled by the calling component
    }
  }

  /**
   * Clear all authentication-related data
   */
  private static clearAllAuthData(): void {
    // Clear tokens from interceptor
    authInterceptor.clearTokens();
    
    // Clear tokens from secure storage
    secureStorage.clearTokens();
    
    // Clear all Supabase-related data
    const authKeys = [
      'poh.access',
      'poh.refresh', 
      'poh.user',
      'poh.refresh.expires',
      'supabase.auth.token',
      // Additional Supabase keys that might persist
      'supabase.auth.refresh_token',
      'supabase.auth.access_token',
      'supabase.auth.user',
      'supabase.auth.session'
    ];
    
    // Clear localStorage and sessionStorage
    authKeys.forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });

    // Clear all keys that start with supabase or sb-
    const allKeys = [...Object.keys(localStorage), ...Object.keys(sessionStorage)];
    allKeys.forEach(key => {
      if (key.startsWith('supabase') || key.startsWith('sb-') || key.startsWith('poh.')) {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      }
    });

    logger.log('All authentication data cleared');
  }

  /**
   * Show appropriate logout message based on reason
   */
  private static showLogoutMessage(reason: string): void {
    const messages = {
      'user_initiated': 'You have been logged out successfully.',
      'token_expired': 'Your session has expired. Please log in again.',
      'session_invalid': 'Your session is no longer valid. Please log in again.',
      'security_breach': 'For security reasons, you have been logged out.'
    };

    const message = messages[reason as keyof typeof messages] || messages.user_initiated;
    
    // You could integrate with a toast notification system here
    logger.log(`Logout message: ${message}`);
    
    // For now, we'll use a simple alert (replace with your notification system)
    if (reason !== 'user_initiated') {
      alert(message);
    }
  }

  /**
   * Force logout (for security scenarios)
   */
  static async forceLogout(reason: 'security_breach' | 'session_invalid' = 'security_breach'): Promise<void> {
    await this.logout({
      showMessage: true,
      reason
    });
  }

  /**
   * Silent logout (for token expiration)
   */
  static async silentLogout(): Promise<void> {
    await this.logout({
      showMessage: false,
      reason: 'token_expired'
    });
  }
}

// Export convenience functions
export const logout = LogoutManager.logout;
export const forceLogout = LogoutManager.forceLogout;
export const silentLogout = LogoutManager.silentLogout;
